import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useGuest } from "../contexts/GuestContext";
import {
  Home,
  Calendar,
  ScanLine,
  ChefHat,
  Tag,
  Wallet,
  Lock,
} from "lucide-react";

const BottomNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPremiumAccess, user } = useAuth();
  const { isGuest, promptSignup } = useGuest();

  // Home always leads to the functional grocery-list dashboard for everyone
  // — signed-in and guests both. This never routes back to the landing page.
  const navItems = [
    { icon: Home, label: "Home", path: "/home", free: true },
    { icon: Calendar, label: "Meal Plans", path: "/meal-plans", free: false, requiresAuth: true },
    { icon: ScanLine, label: "Scan", path: "/scan", free: true },
    { icon: ChefHat, label: "Recipes", path: "/recipes", free: false, requiresAuth: true },
    // Deals is guest-viewable (Grocery List Saver + Community Deals sub-tabs)
    { icon: Tag, label: "Deals", path: "/deals", free: true },
    // Budget is guest-viewable; receipt scan/upload is gated inside the page
    { icon: Wallet, label: "Budget", path: "/budget", free: true },
  ];

  const handleNav = (item) => {
    // Guest hitting an auth-required tab → open signup prompt
    if (item.requiresAuth && isGuest) {
      promptSignup({
        headline: `Sign up to unlock ${item.label}`,
        subhead: "Start your 14-day free All-Access trial. No card, cancel anytime.",
      });
      return;
    }
    // Signed-in but not premium → subscribe funnel
    if (!item.free && !hasPremiumAccess) {
      navigate("/subscription");
      return;
    }
    navigate(item.path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800 px-2 py-2" data-testid="bottom-nav">
      <div className="flex justify-around items-center">
        {navItems.map((item) => {
          const { icon: Icon, label, path, free, requiresAuth } = item;
          const isActive = location.pathname === path;
          const guestLocked = requiresAuth && isGuest;
          const premiumLocked = !free && !hasPremiumAccess && !isGuest;
          const isLocked = guestLocked || premiumLocked;
          return (
            <Button
              key={label}
              variant="ghost"
              size="sm"
              onClick={() => handleNav(item)}
              className={`flex flex-col items-center gap-1 px-2 py-1 h-auto text-xs transition-all duration-200 relative ${
                isActive
                  ? "text-green-400 bg-gray-800"
                  : isLocked
                  ? "text-gray-600 hover:text-gray-500 hover:bg-gray-800"
                  : "text-gray-400 hover:text-white hover:bg-gray-800"
              }`}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {isLocked && (
                  <Lock className="h-2.5 w-2.5 absolute -top-1 -right-1.5 text-amber-500" />
                )}
              </div>
              <span className="text-[10px] font-medium">{label}</span>
            </Button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
